import React, { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { createBrowserRouter, LoaderFunctionArgs, Navigate, NonIndexRouteObject, Outlet, RouterProvider } from 'react-router-dom';
import BaseLayout from 'layouts/base-layout';
import Routes from 'shared/enums/routes';
import SigninRedirect from 'shared/features/auth/authentication/components/signin-redirect/signin-redirect';
import SignoutRedirect from 'shared/features/auth/authentication/components/signout-redirect/signout-redirect';
import authGuard from 'shared/features/auth/authentication/hooks/auth-guard';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import RouteRedirector from 'shared/features/auth/authorization/route-redirector';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';
import { DevicesProvider } from 'shared/providers/devices/providers/devices-provider';

import AppInformation from 'features/app-information/views/app-information-view';
import CareConnectSettings from 'features/care-connect-settings/views/care-connect-settings';
import { ConnectorsProvider } from 'features/connectors/context/connectors-provider';
import Connectors from 'features/connectors/views/connectors';
import Dashboard from 'features/dashboard/views/dashboard';
import Devices from 'features/device/devices/views/devices.component';
import { SendTestEventProvider } from 'features/device/send-test-event/context/send-test-event-provider';
import { EmergencyAlarmSettingsProvider } from 'features/emergency-alarm-settings/context/emergency-alarm-settings-provider';
import EmergencyAlarmSettings from 'features/emergency-alarm-settings/views/emergency-alarm-settings';
import { ExplanationLinksProvider } from 'features/explanation-links/context/explanation-links-provider';
import ExplanationLinks from 'features/explanation-links/views/explanation-links';
import { FaultsHistoryProvider } from 'features/faults-history/context/faults-history-provider';
import FaultsHistory from 'features/faults-history/views/faults-history';
import FeatureSubscriptions from 'features/feature-subscriptions/feature-subscriptions';
import FeatureSubscriptionsEditingProvider from 'features/feature-subscriptions/providers/feature-subscriptions-editing-provider';
import LocationFeatureSubscriptionsProvider from 'features/feature-subscriptions/providers/location-feature-subscriptions-provider';
import TenantFeatureSubscriptionsProvider from 'features/feature-subscriptions/providers/tenant-feature-subscriptions-provider';
import { GatewaysProvider } from 'features/gateway/context/gateway-provider';
import Gateways from 'features/gateway/views/gateways';
import { HistoryProvider } from 'features/history/providers/history-provider';
import History from 'features/history/views/history.component';
import Login from 'features/login/login';
import { MalfunctionNotificationSettingsProvider } from 'features/malfunction-notification-settings/context/malfunction-notification-settings-provider';
import MalfunctionNotificationSettings from 'features/malfunction-notification-settings/views/malfunction-notification-settings';
import MediaserverSettings from 'features/mediaserver-settings/views/mediaserver-settings';
import { OrganisationUnitsProvider } from 'features/organisation/context/organisation-units-provider';
import Organisation from 'features/organisation/views/organisation';
import ResidentsChangeLogProvider from 'features/residents/resident-change-logs/providers/residents-change-log-provider';
import ResidentsChangeLog from 'features/residents/resident-change-logs/views/residents-change-log';
import ResidentSettingsProvider from 'features/residents/resident-settings/providers/resident-settings-provider';
import ResidentSettings from 'features/residents/resident-settings/resident-settings';
import Roles from 'features/role/views/roles';
import RootPage from 'features/root/root-page';
import { SystemsProvider } from 'features/systems/context/systems-provider';
import Systems from 'features/systems/views/systems';
import { TemporaryAccessProvider } from 'features/temporary-access/providers/temporary-access-provider';
import TemporaryAccess from 'features/temporary-access/views/temporary-access.component';
import TenantsProvider from 'features/tenants/providers/tenants-provider/tenants-provider';
import Tenants from 'features/tenants/tenants';
import WanderingBoundarySettingsProvider from 'features/wandering-boundary-settings/providers/wandering-boundary-settings-provider';
import WanderingBoundarySettings from 'features/wandering-boundary-settings/views/wandering-boundary-settings';

const Router = (): React.JSX.Element => {
  const { hasRoutePermission } = usePermissionAuthorization();
  const auth = useAuth();

  const [failedSilentSignIn, setFailedSilentSignIn] = React.useState(false);

  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading && !failedSilentSignIn) {
      auth
        .signinSilent()
        .then((user) => {
          if (user === null) {
            setFailedSilentSignIn(true);
          }
        })
        .catch(() => {
          setFailedSilentSignIn(true);
        });
    }
  }, [auth]);

  const routeLoader = (route: Routes, subLoader?: NonIndexRouteObject['loader']): NonIndexRouteObject['loader'] => {
    return async (args: LoaderFunctionArgs) => {
      const response = authGuard(auth);
      if (response !== null) {
        return response;
      }

      if (auth.isLoading) {
        return {};
      }

      if (hasRoutePermission(route)) {
        return subLoader && typeof subLoader === 'function' ? subLoader(args) : {};
      }

      throw Response.json({}, { status: 404 });
    };
  };

  const router = createBrowserRouter([
    {
      path: '',
      element: <RootPage />,
      errorElement: <Navigate to={Routes.Login} />,
    },
    {
      path: Routes.Login,
      element: <Login />,
    },
    {
      path: Routes.SignInRedirect,
      element: <SigninRedirect />,
    },
    {
      path: Routes.SignOutRedirect,
      element: <SignoutRedirect />,
    },
    {
      element: (
        <BaseLayout>
          <Outlet />
        </BaseLayout>
      ),
      children: [
        {
          path: Routes.Tenants,
          element: (
            <TenantsProvider>
              <Tenants />
            </TenantsProvider>
          ),
          loader: routeLoader(Routes.Tenants),
        },
        {
          path: Routes.FeatureSubscriptions,
          element: (
            <FeatureSubscriptionsEditingProvider>
              <LocationFeatureSubscriptionsProvider>
                <TenantFeatureSubscriptionsProvider>
                  <FeatureSubscriptions />
                </TenantFeatureSubscriptionsProvider>
              </LocationFeatureSubscriptionsProvider>
            </FeatureSubscriptionsEditingProvider>
          ),
          loader: routeLoader(Routes.FeatureSubscriptions),
        },
        {
          path: Routes.Organisation,
          element: (
            <OrganisationUnitsProvider>
              <Organisation />
            </OrganisationUnitsProvider>
          ),
          loader: routeLoader(Routes.Organisation),
        },
        {
          path: Routes.Residents,
          element: <Outlet />,
          loader: routeLoader(Routes.Residents),
          children: [
            {
              path: '',
              element: <RouteRedirector routeDomain="Residents" />,
            },
            {
              path: Routes.ResidentsSettings,
              element: (
                <OrganisationUnitTreeProvider.AccessRightsOnly excludeBranchesWithoutResidents>
                  <ResidentSettingsProvider>
                    <ResidentSettings />
                  </ResidentSettingsProvider>
                </OrganisationUnitTreeProvider.AccessRightsOnly>
              ),
              loader: routeLoader(Routes.ResidentsSettings),
            },
            {
              path: Routes.ResidentsChangeLog,
              element: (
                <ResidentsChangeLogProvider>
                  <ResidentsChangeLog />
                </ResidentsChangeLogProvider>
              ),
              loader: routeLoader(Routes.ResidentsChangeLog),
            },
          ],
        },
        {
          path: Routes.Roles,
          element: <Roles />,
          loader: routeLoader(Routes.Roles),
        },
        {
          path: Routes.History,
          element: (
            <HistoryProvider>
              <History />
            </HistoryProvider>
          ),
          loader: routeLoader(Routes.History),
        },
        {
          path: Routes.FaultsHistory,
          element: (
            <FaultsHistoryProvider>
              <FaultsHistory />
            </FaultsHistoryProvider>
          ),
          loader: routeLoader(Routes.FaultsHistory),
        },
        {
          path: Routes.Dashboard,
          element: <Dashboard />,
          loader: routeLoader(Routes.Dashboard),
        },
        {
          path: Routes.AppInformation,
          element: <AppInformation />,
          loader: routeLoader(Routes.AppInformation),
        },
        {
          path: Routes.Integrations,
          element: (
            <OrganisationUnitTreeProvider.All>
              <Outlet />
            </OrganisationUnitTreeProvider.All>
          ),
          loader: routeLoader(Routes.Integrations),
          children: [
            {
              path: '',
              element: <RouteRedirector routeDomain="Integrations" />,
            },
            {
              path: Routes.IntegrationsDevices,
              element: (
                <DevicesProvider>
                  <SendTestEventProvider>
                    <Devices />
                  </SendTestEventProvider>
                </DevicesProvider>
              ),
              loader: routeLoader(Routes.IntegrationsDevices),
            },
            {
              path: Routes.IntegrationsSystems,
              element: (
                <DevicesProvider>
                  <SystemsProvider>
                    <Systems />
                  </SystemsProvider>
                </DevicesProvider>
              ),
              loader: routeLoader(Routes.IntegrationsSystems),
            },
            {
              path: Routes.IntegrationsGateways,
              element: (
                <GatewaysProvider>
                  <Gateways />
                </GatewaysProvider>
              ),
              loader: routeLoader(Routes.IntegrationsGateways),
            },
            {
              path: Routes.IntegrationsConnectors,
              element: (
                <ConnectorsProvider>
                  <Connectors />
                </ConnectorsProvider>
              ),
              loader: routeLoader(Routes.IntegrationsConnectors),
            },
          ],
        },
        {
          path: Routes.TemporaryAccess,
          element: (
            <TemporaryAccessProvider>
              <TemporaryAccess />
            </TemporaryAccessProvider>
          ),
          loader: routeLoader(Routes.TemporaryAccess),
        },
        {
          path: Routes.Settings,
          element: <Outlet />,
          children: [
            {
              path: '',
              element: <RouteRedirector routeDomain="Settings" />,
            },
            {
              path: Routes.SettingsExplanationLink,
              element: (
                <ExplanationLinksProvider>
                  <ExplanationLinks />
                </ExplanationLinksProvider>
              ),
              loader: routeLoader(Routes.SettingsExplanationLink),
            },
            {
              path: Routes.SettingsEmergencyAlarm,
              element: (
                <EmergencyAlarmSettingsProvider>
                  <EmergencyAlarmSettings />
                </EmergencyAlarmSettingsProvider>
              ),
              loader: routeLoader(Routes.SettingsEmergencyAlarm),
            },
            {
              path: Routes.SettingsMalfunctionNotifications,
              element: (
                <MalfunctionNotificationSettingsProvider>
                  <MalfunctionNotificationSettings />
                </MalfunctionNotificationSettingsProvider>
              ),
              loader: routeLoader(Routes.SettingsMalfunctionNotifications),
            },
            {
              path: Routes.SettingsWanderingBoundaries,
              element: (
                <WanderingBoundarySettingsProvider>
                  <WanderingBoundarySettings />
                </WanderingBoundarySettingsProvider>
              ),
              loader: routeLoader(Routes.SettingsWanderingBoundaries),
            },
            {
              path: Routes.SettingsCareConnect,
              element: <CareConnectSettings />,
              loader: routeLoader(Routes.SettingsCareConnect),
            },
            {
              path: Routes.SettingsMediaserverSwitch,
              element: <MediaserverSettings />,
              loader: routeLoader(Routes.SettingsMediaserverSwitch),
            },
          ],
        },
      ],
      errorElement: <Navigate to={Routes.Default} />,
    },
  ]);

  return <RouterProvider router={router} />;
};

export default Router;
